import postGenerateResourceURL from '@cartwhl/common/api/postGenerateResourceURL';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import type {
  FilesListFile,
  FilesListHandle,
} from '@cartwhl/ui/input/FilesList';

import * as globalStores from '../../stores/global';

export function getUploadCdnBase(): string {
  const env = import.meta.env.VITE_API_DISAMBIGUATOR ?? '';
  return env === ''
    ? 'https://resources.getcartwheel.com/'
    : 'https://resources-staging.getcartwheel.com/';
}

export const CDN_BASE = getUploadCdnBase();
export const CDN_DEFAULT_PATH = 'share/demo-videos/';

export type UploadResult = {
  resourcePath: string;
  fullUrl: string;
  fileName: string;
};

export type UploadStatus =
  | { phase: 'idle' }
  | {
      phase: 'uploading';
      current: number;
      total: number;
      currentFileName: string;
    };

type Deps = { global: typeof globalStores };

type State = {
  prefix: string;
  fileIds: string[];
  uploadStatus: UploadStatus;
  results: UploadResult[];
};

const initialState: State = {
  prefix: CDN_DEFAULT_PATH,
  fileIds: [],
  uploadStatus: { phase: 'idle' },
  results: [],
};

function sanitizeFilename(name: string): string {
  return name.replace(/\s+/g, '-');
}

function buildResourcePath(prefix: string, fileName: string): string {
  const normalized = prefix.trim() || CDN_DEFAULT_PATH;
  const withSlash = normalized.endsWith('/') ? normalized : `${normalized}/`;
  return withSlash + sanitizeFilename(fileName);
}

class AdminAssetUploadStore extends BaseStore<State, Deps> {
  private filesListRefGetter: (() => FilesListHandle | null) | null = null;

  constructor(deps: Deps) {
    super(initialState, deps);
  }

  init() {}

  reset() {
    this.state.prefix = initialState.prefix;
    this.state.fileIds = [];
    this.state.uploadStatus = { phase: 'idle' };
    this.state.results = [];
    this.getFilesListRef()?.clearAll();
  }

  destroy() {
    super.destroy();
  }

  setFilesListRef(getter: () => FilesListHandle | null) {
    this.filesListRefGetter = getter;
  }

  private getFilesListRef(): FilesListHandle | null {
    return this.filesListRefGetter?.() ?? null;
  }

  setPrefix(prefix: string) {
    this.state.prefix = prefix;
  }

  validatePath(): boolean {
    const p = this.state.prefix.trim() || CDN_DEFAULT_PATH;
    return p.startsWith('share/') && p.endsWith('/');
  }

  syncFiles(files: FilesListFile[]) {
    this.state.fileIds = files.map((f) => f.id);
  }

  getFileIndex(id: string): number {
    return this.state.fileIds.indexOf(id);
  }

  clearAll() {
    this.state.fileIds = [];
    this.getFilesListRef()?.clearAll();
  }

  async uploadAll() {
    if (!this.validatePath()) {
      this.deps.global.noticesStore.error(
        'Resource path must start with "share/" and end with "/"',
      );
      return;
    }

    const allFiles = this.getFilesListRef()?.getFiles() ?? [];
    if (allFiles.length === 0) return;

    const alreadyUploadedNames = new Set(
      this.state.results.map((r) => r.fileName),
    );
    const filesToUpload = allFiles.filter(
      (f) => !alreadyUploadedNames.has(f.file.name),
    );

    if (filesToUpload.length === 0) return;

    this.state.uploadStatus = {
      phase: 'uploading',
      current: 0,
      total: filesToUpload.length,
      currentFileName: '',
    };

    const newResults: UploadResult[] = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      const listFile = filesToUpload[i];
      try {
        this.state.uploadStatus = {
          phase: 'uploading',
          current: i + 1,
          total: filesToUpload.length,
          currentFileName: listFile.file.name,
        };
        const resourcePath = buildResourcePath(
          this.state.prefix,
          listFile.file.name,
        );

        const { data, error } = await postGenerateResourceURL({
          resourcePath,
        });

        if (error || !data) {
          const message = error?.message ?? 'Unknown error';
          this.deps.global.noticesStore.error(
            `Failed to get upload URL for "${listFile.file.name}": ${message}`,
          );
          continue;
        }

        const putRes = await fetch(data.uploadURL, {
          method: 'PUT',
          body: listFile.file,
          headers: { 'Content-Type': listFile.file.type },
        });

        if (!putRes.ok) {
          let statusMsg = `${putRes.status} ${putRes.statusText}`;
          if (putRes.status === 403) {
            statusMsg =
              '403 Forbidden (check bucket policy or CORS on the bucket)';
          } else if (putRes.status === 0) {
            statusMsg =
              'Request blocked or failed (often CORS – check bucket CORS config)';
          }
          this.deps.global.noticesStore.error(
            `Failed to upload "${listFile.file.name}": ${statusMsg}`,
          );
          continue;
        }

        const fullUrl = CDN_BASE ? `${CDN_BASE}${data.resourcePath}` : '';
        newResults.push({
          resourcePath: data.resourcePath,
          fullUrl,
          fileName: listFile.file.name,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        this.deps.global.noticesStore.error(
          `Failed to upload "${filesToUpload[i].file.name}": ${message}`,
        );
      }
    }

    this.state.results = [...newResults, ...this.state.results];
    this.state.uploadStatus = { phase: 'idle' };
  }
}

export const { useStore, withProvider, Context } = createStoreProvider({
  Store: AdminAssetUploadStore,
  displayName: 'AdminAssetUploadStore',
  staticDeps: { global: globalStores },
});
